/**
 * Tool registry for /tools/* — single source of truth for slug, copy, SEO
 * metadata, and category. Imported by both the React route components and
 * the Cloudflare Worker (HTMLRewriter) so crawlers see unique
 * <title>/<meta description> per route without SSG.
 *
 * Pure data only — no React, no lucide imports — so the Worker bundle
 * stays small.
 */

export type ToolCategory =
  | 'organize'
  | 'optimize'
  | 'accessibility'
  | 'convert-to'
  | 'convert-from'
  | 'edit';

export type ToolProcessingType = 'local' | 'cloud';

export interface ToolDef {
  slug: string;
  iconName: string;
  name: string;
  description: string;
  category: ToolCategory;
  processingType: ToolProcessingType;
  /** SEO <title>. Keep < 60 chars. Lead with the keyword. */
  metaTitle: string;
  /** SEO meta description. Keep < 155 chars. Action-led, value-first. */
  metaDescription: string;
  /** On-page H1. Slightly different from metaTitle (no brand suffix). */
  h1: string;
  /** Optional 1-2 sentence subhead under H1. */
  subhead?: string;
  /**
   * Optional header badge in place of the processing label (Cloud processing /
   * Browser only), when what the visitor gets says more than where it runs.
   */
  badge?: string;
  /** 3-5 short bullets that double as crawler-readable feature copy. */
  bullets: string[];
  /** Optional how-to steps. Rendered as an ordered list + HowTo JSON-LD. */
  steps?: string[];
  /** Optional FAQ entries. Rendered on-page + FAQPage JSON-LD. */
  faq?: { q: string; a: string }[];
  /** Optional long-form tutorial on /blog. Tool pages link out; the post links back. */
  guide?: { href: string; label: string };
}

export const CATEGORY_LABELS: Record<ToolCategory | 'all', string> = {
  all: 'All Tools',
  organize: 'Organize PDF',
  edit: 'Edit PDF',
  accessibility: 'Accessibility checks',
  'convert-to': 'Convert to PDF',
  'convert-from': 'Convert from PDF',
  optimize: 'Optimize PDF',
};

/**
 * Category sections on /tools, ordered by real usage (PostHog `tool_viewed`
 * unique visitors, 60-day window): convert tools dominate, the compliance
 * checkers trail far behind, so accessibility renders last.
 */
export const CATEGORY_ORDER: ToolCategory[] = [
  'convert-to',
  'convert-from',
  'organize',
  'optimize',
  'edit',
  'accessibility',
];

/**
 * "Popular tools" rail on /tools — ranked by unique visitors on tool pages
 * (PostHog `tool_viewed`, 60-day window, measured 2026-08-23). Re-rank when
 * the numbers move. Entries must have a working runner page (no
 * coming-soon placeholders), which is why `extract` is skipped.
 */
export const POPULAR_TOOL_SLUGS: string[] = [
  'jpg-to-pdf',
  'pdf-to-link',
  'merge',
  'pdf-to-json',
  'flatten',
  'pdf-to-html',
];

export const TOOLS: ToolDef[] = [
  // Organize
  {
    slug: 'organize',
    iconName: 'LayoutGrid',
    name: 'Organize PDF',
    description: 'Rearrange, delete, and reorder pages.',
    category: 'organize',
    processingType: 'local',
    metaTitle: 'Organize PDF — Reorder & Delete Pages | okraPDF',
    metaDescription: 'Rearrange, delete, and reorder PDF pages right in your browser. No upload, no signup — files never leave your device.',
    h1: 'Organize PDF pages',
    subhead: 'Drag to reorder, click to delete. Save the result locally.',
    bullets: [
      'Visual page thumbnails — drag to reorder',
      'Delete individual pages or ranges',
      '100% in-browser — your file never uploads',
      'Free, no signup, no watermark',
    ],
    steps: [
      'Drop your PDF above to see every page as a thumbnail.',
      'Drag pages to reorder, or select and delete the ones you don’t need.',
      'Click Save and download the reorganized PDF — all done in your browser.',
    ],
    faq: [
      {
        q: 'Is my file uploaded?',
        a: 'No. Reordering and deleting pages happens locally in your browser; the file never leaves your device.',
      },
      {
        q: 'Can I delete multiple pages at once?',
        a: 'Yes. Select any pages or ranges and remove them in one step before saving.',
      },
      {
        q: 'Will reordering change the quality?',
        a: 'No. Pages are rearranged losslessly — text, images, and resolution stay exactly as in the original.',
      },
    ],
  },
  {
    slug: 'merge',
    iconName: 'Layers',
    name: 'Merge PDF',
    description: 'Combine multiple PDFs into one.',
    category: 'organize',
    processingType: 'local',
    metaTitle: 'Merge PDF — Combine Files Online Free | okraPDF',
    metaDescription: 'Merge multiple PDF files into one document. Drag to reorder, runs entirely in your browser. No size limits, no signup.',
    h1: 'Merge PDF files',
    subhead: 'Drag and drop multiple PDFs, reorder them, then download a single combined file.',
    bullets: [
      'Combine unlimited PDFs into one',
      'Drag-and-drop to reorder before merging',
      '100% in-browser — files never upload',
      'No file size limits, no watermark',
    ],
    steps: [
      'Drop your PDF files into the box above, or click to browse.',
      'Use the arrow buttons or drag files to set the order you want.',
      'Click Merge and download the combined PDF — it never leaves your browser.',
    ],
    faq: [
      {
        q: 'Is merging PDFs in the browser safe?',
        a: 'Yes. Merging runs entirely in your browser, so your files are never uploaded to a server and nothing leaves your device.',
      },
      {
        q: 'Is there a file size or page limit?',
        a: 'No. Because merging happens locally, there are no upload caps — the only limit is your device’s available memory.',
      },
      {
        q: 'Can I reorder pages before merging?',
        a: 'Yes. Drag the file thumbnails into any order before merging, and the combined PDF follows that sequence.',
      },
      {
        q: 'Do I need to sign up or pay?',
        a: 'No. The merge tool is free with no account, no watermark, and no email required.',
      },
    ],
  },
  {
    slug: 'split',
    iconName: 'Scissors',
    name: 'Split PDF',
    description: 'Split a PDF by page ranges.',
    category: 'organize',
    processingType: 'local',
    metaTitle: 'Split PDF — Extract Pages & Page Ranges | okraPDF',
    metaDescription: 'Split a PDF into multiple files by page range, or extract every page as its own document. Free, in-browser, no signup.',
    h1: 'Split PDF by pages',
    bullets: [
      'Split by page ranges (e.g. 1-3, 5, 8-10)',
      'Or extract every page as a separate PDF',
      '100% in-browser — files never upload',
      'No watermark, no file size limits',
    ],
  },

  // Optimize
  {
    slug: 'compress',
    iconName: 'FileDown',
    name: 'Compress PDF',
    description: 'Reduce PDF file size while keeping quality.',
    category: 'optimize',
    processingType: 'local',
    metaTitle: 'Compress PDF — Reduce File Size Online | okraPDF',
    metaDescription: 'Compress PDF files to reduce size while keeping quality. Runs in your browser — files never leave your device. Free, no signup.',
    h1: 'Compress PDF',
    subhead: 'Shrink PDF size for email or web upload, with quality presets.',
    bullets: [
      'Smart compression — keeps text crisp',
      'Choose quality: low, medium, or high',
      '100% in-browser — files never upload',
      'No watermark, no file size limits',
    ],
  },
  {
    slug: 'flatten',
    iconName: 'Layers',
    name: 'Flatten PDF',
    description: 'Convert form fields and annotations to static content.',
    category: 'optimize',
    processingType: 'local',
    metaTitle: 'Flatten PDF — Lock Forms & Annotations | okraPDF',
    metaDescription: 'Flatten PDF form fields, annotations, and comments into static content. Make PDFs uneditable for archival or signing. Free, browser-only.',
    h1: 'Flatten PDF',
    bullets: [
      'Lock form fields into static content',
      'Bake in annotations and comments',
      'Ideal for archival or signed documents',
      '100% in-browser — files never upload',
    ],
  },
  {
    slug: 'ocr',
    iconName: 'ScanText',
    name: 'OCR PDF',
    description: 'Make scanned PDFs searchable.',
    category: 'optimize',
    processingType: 'local',
    metaTitle: 'OCR PDF — Make Scanned PDFs Searchable | okraPDF',
    metaDescription: 'Run OCR on scanned PDFs to extract text and make them searchable. Powered by Tesseract — runs entirely in your browser. Free, no upload.',
    h1: 'OCR a scanned PDF',
    subhead: 'Extract text from scanned PDFs and make them searchable. Local OCR via Tesseract.js.',
    bullets: [
      'Tesseract.js OCR — 100+ languages',
      'Adds searchable text layer to scans',
      '100% in-browser — files never upload',
      'Free, no signup, no watermark',
    ],
  },
  {
    slug: 'compare',
    iconName: 'GitCompare',
    name: 'Compare PDF',
    description: 'Compare two PDFs side by side.',
    category: 'optimize',
    processingType: 'local',
    metaTitle: 'Compare PDF — Side-by-Side Diff Tool | okraPDF',
    metaDescription: 'Compare two PDF documents side by side and highlight differences visually. Free, in-browser, no upload.',
    h1: 'Compare two PDFs',
    bullets: [
      'Side-by-side visual comparison',
      'Highlights added, removed, or changed text',
      '100% in-browser — files never upload',
      'Free, no signup',
    ],
  },
  {
    slug: 'metadata',
    iconName: 'ShieldCheck',
    name: 'PDF Inspector',
    description: 'View metadata, security, fonts, and document properties.',
    category: 'optimize',
    processingType: 'local',
    metaTitle: 'PDF Inspector — View Metadata, Security, Fonts | okraPDF',
    metaDescription: 'Inspect PDF metadata, security settings, fonts, and document properties. See exactly what your PDF reveals. Free, browser-only.',
    h1: 'Inspect PDF metadata',
    bullets: [
      'View all metadata fields (author, producer, dates)',
      'Check encryption and password protection',
      'List embedded fonts and document properties',
      '100% in-browser — files never upload',
    ],
  },

  // Accessibility
  {
    slug: 'verapdf-checker',
    iconName: 'ShieldCheck',
    name: 'veraPDF Checker',
    description: 'Check PDF/UA signals before running a full veraPDF validation.',
    category: 'accessibility',
    processingType: 'cloud',
    metaTitle: 'veraPDF Checker — PDF/UA Preflight | okraPDF',
    metaDescription:
      'Run an actor-backed PDF/UA preflight workflow for veraPDF-style failures: tags, language, title, alt text, and structure.',
    h1: 'veraPDF PDF/UA checker',
    subhead:
      'Upload a PDF and get a workflow-backed PDF/UA preflight report before you run a formal veraPDF or PAC validation.',
    bullets: [
      'Checks tags, language, title, text layer, parent tree, MCIDs, and alt-text signals',
      'Flags issues that commonly fail PDF/UA validators',
      'Exports a JSON evidence report for remediation handoff',
      'Proxied actor run with pollable workflow progress',
    ],
    steps: [
      'Drop a PDF into the checker.',
      'Review failed, warning, and human-review findings.',
      'Export the JSON report or use it as a preflight before a formal veraPDF run.',
    ],
    faq: [
      {
        q: 'Does this run the official veraPDF CLI?',
        a: 'No. This starts an okraPDF workflow that proxies the PDF to the accessibility checker actor for machine-detectable PDF/UA signals. Use the exported report as preflight evidence, then run official veraPDF/PAC for formal validation.',
      },
      {
        q: 'Can this prove WCAG or PDF/UA compliance?',
        a: 'No single automated checker can prove full compliance. This tool separates machine-detectable issues from human-review items such as reading order and meaningful alt text.',
      },
    ],
  },
  {
    slug: 'matterhorn-pdf-checker',
    iconName: 'ShieldCheck',
    name: 'Matterhorn PDF Checker',
    description: 'Preflight PDF accessibility against Matterhorn-style failure areas.',
    category: 'accessibility',
    processingType: 'local',
    metaTitle: 'Matterhorn PDF Checker — Accessibility Preflight | okraPDF',
    metaDescription:
      'Check PDF accessibility against Matterhorn-style areas: tags, language, headings, tables, figures, forms, and reading-order review.',
    h1: 'Matterhorn Protocol PDF checker',
    subhead:
      'Run a local preflight mapped to the main Matterhorn/PDF/UA failure areas, with human-review items called out.',
    bullets: [
      'Maps findings to structural, metadata, content, semantic, and review areas',
      'Separates machine failures from human-review checks',
      'Useful for Section 508 and PDF/UA remediation triage',
      'Runs locally in the browser and exports JSON',
    ],
    steps: [
      'Upload a PDF.',
      'Review the Matterhorn-style failure areas and human-review flags.',
      'Use the findings to decide whether to fix source, remediate tags, or request manual review.',
    ],
    faq: [
      {
        q: 'Is this the full Matterhorn Protocol?',
        a: 'No. Matterhorn has many detailed checks, including conditions that require human judgment. This page is a local preflight that highlights the major failure areas and marks judgment calls explicitly.',
      },
      {
        q: 'Why are some findings marked human review?',
        a: 'Reading order, alt-text quality, table meaning, and form instructions cannot be proven from syntax alone. They need reviewer or screen-reader validation.',
      },
    ],
  },
  {
    slug: 'well-tagged-pdf-checker',
    iconName: 'ShieldCheck',
    name: 'Well-Tagged PDF Checker',
    description: 'Check whether a PDF has the tags screen readers need.',
    category: 'accessibility',
    processingType: 'local',
    metaTitle: 'Well-Tagged PDF Checker — Tags & Reading Order | okraPDF',
    metaDescription:
      'Check whether a PDF has accessibility tags for headings, lists, tables, figures, links, text, language, and reading-order review.',
    h1: 'Well-tagged PDF checker',
    subhead:
      'Check the tag signals that make a PDF navigable for screen readers: headings, lists, tables, figures, links, and language.',
    bullets: [
      'Finds missing tag-tree, marked-content, heading, list, table, and figure signals',
      'Flags image-only scans and missing text layer',
      'Calls out reading-order and alt-text quality as human-review items',
      'Runs locally with no PDF upload',
    ],
    steps: [
      'Drop in a PDF.',
      'Check whether it has the structural tags a screen reader depends on.',
      'Use the report to fix source files or repair the PDF tag tree.',
    ],
    faq: [
      {
        q: 'What does well-tagged mean?',
        a: 'A well-tagged PDF has a logical structure tree, real text, document language and title, headings, lists, table semantics, figure alt text, links, and a reading order that matches the visual meaning.',
      },
      {
        q: 'Can a tagged PDF still be inaccessible?',
        a: 'Yes. Tags can be present but semantically wrong. This checker flags syntax signals and marks semantic checks that still need human review.',
      },
    ],
  },

  // Convert to PDF
  {
    slug: 'word-to-pdf',
    iconName: 'FileText',
    name: 'Word to PDF',
    description: 'Convert Word documents to PDF.',
    category: 'convert-to',
    processingType: 'local',
    metaTitle: 'Word to PDF — Convert DOCX Online Free | okraPDF',
    metaDescription: 'Convert DOCX Word documents to clean PDF text in your browser. Free, no upload, no signup, no watermark.',
    h1: 'Convert Word to PDF',
    subhead: 'Upload a .docx and get a clean PDF text copy directly in your browser.',
    bullets: [
      'Extracts readable Word document text',
      'Supports .docx Word documents',
      'Runs locally in your browser',
      'Free, no signup',
    ],
    steps: [
      'Upload your .docx file.',
      'We extract the document text locally and place it into a PDF.',
      'Download your PDF.',
    ],
    faq: [
      {
        q: 'Will my formatting be preserved?',
        a: 'This browser version prioritizes readable text. Complex Word layout, fonts, tables, and images may not be preserved.',
      },
      {
        q: 'Does it support both .doc and .docx?',
        a: 'The browser converter supports .docx. Save older .doc files as .docx first, then upload the converted file.',
      },
      {
        q: 'Is it free?',
        a: 'Yes — no signup or watermark. Conversion runs in your browser, so the document is not uploaded.',
      },
    ],
  },
  {
    slug: 'excel-to-pdf',
    iconName: 'FileSpreadsheet',
    name: 'Excel to PDF',
    description: 'Convert Excel spreadsheets to PDF.',
    category: 'convert-to',
    processingType: 'cloud',
    metaTitle: 'Excel to PDF — Convert XLSX Online Free | okraPDF',
    metaDescription: 'Convert Excel spreadsheets (.xlsx) to clean shareable PDFs. Auto-fits page width, preserves formatting. Free, fast.',
    h1: 'Convert Excel to PDF',
    bullets: [
      'Auto-fits column widths to the page',
      'Preserves number formatting and merged cells',
      'Files auto-deleted after 24 hours',
      'Free, no signup',
    ],
  },
  {
    slug: 'jpg-to-pdf',
    iconName: 'Image',
    name: 'JPG to PDF',
    description: 'Convert JPG, PNG, and WebP images to PDF.',
    category: 'convert-to',
    processingType: 'local',
    metaTitle: 'JPG to PDF — Convert Images to PDF Free | okraPDF',
    metaDescription: 'Convert JPG, PNG, and WebP images to PDF in your browser. Reorder pages, choose A4, Letter, or fit-to-image with margins, then download — no upload required.',
    h1: 'Convert JPG to PDF',
    subhead: 'Drop your images, set the page size, drag to reorder, and download a single PDF.',
    bullets: [
      'Supports JPG, PNG, WebP, GIF',
      'Page size: fit-to-image, A4, or US Letter — with margins',
      'Drag and drop to reorder pages',
      '100% in-browser — files never upload',
      'No file count or size limits',
    ],
    steps: [
      'Choose JPG, PNG, WebP, or GIF images, or drop them into the box above.',
      'Use the arrows or drag to reorder, then pick a page size, orientation, and margin.',
      'Click Convert and download a single PDF, generated right in your browser.',
    ],
    faq: [
      {
        q: 'Are my images uploaded anywhere?',
        a: 'No. Conversion happens locally in your browser, so your photos never leave your device. You can even disconnect from the internet and it still works.',
      },
      {
        q: 'Can I combine multiple images into one PDF?',
        a: 'Yes. Add as many images as you like, reorder them, and they are merged into a single multi-page PDF.',
      },
      {
        q: 'Can I make A4 or US Letter pages?',
        a: 'Yes. Choose fit-to-image (each page matches the photo), A4, or US Letter, with auto, portrait, or landscape orientation and optional margins. Images are scaled to fit and centered.',
      },
      {
        q: 'Which image formats are supported?',
        a: 'JPG/JPEG, PNG, WebP, and GIF. Each image is placed on its own page.',
      },
      {
        q: 'Will the image quality drop?',
        a: 'No. JPG and PNG images are embedded at their original resolution with no recompression.',
      },
    ],
  },
  {
    slug: 'png-to-pdf',
    iconName: 'Image',
    name: 'PNG to PDF',
    description: 'Convert PNG images to PDF.',
    category: 'convert-to',
    processingType: 'local',
    metaTitle: 'PNG to PDF — Convert PNG Images Free | okraPDF',
    metaDescription: 'Convert PNG images to PDF online. Drag-drop, reorder, and download — runs in your browser, no upload required.',
    h1: 'Convert PNG to PDF',
    bullets: [
      'Preserves transparency where possible',
      'Page size: fit-to-image, A4, or US Letter — with margins',
      'Drag and drop to reorder pages',
      '100% in-browser — files never upload',
      'Free, no signup, no watermark',
    ],
  },

  // Convert from PDF
  {
    slug: 'pdf-to-word',
    iconName: 'FileText',
    name: 'PDF to Word',
    description: 'Convert PDFs to editable Word documents.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to Word — Convert to Editable DOCX | okraPDF',
    metaDescription: 'Convert PDF files to editable Word documents (.docx). Preserves text, layout, and tables. Free, fast, files deleted after 24h.',
    h1: 'Convert PDF to Word',
    subhead: 'Get an editable .docx with text, layout, and tables preserved.',
    bullets: [
      'Preserves text formatting and tables',
      'Outputs .docx editable in Word, Pages, or Google Docs',
      'Files auto-deleted after 24 hours',
      'Free, no signup',
    ],
  },
  {
    slug: 'pdf-to-excel',
    iconName: 'FileSpreadsheet',
    name: 'PDF to Excel',
    description: 'Extract PDF tables into Excel.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to Excel — Extract Tables to XLSX | okraPDF',
    metaDescription:
      'Extract tables from PDF financial reports, bank statements, and invoices into Excel with LlamaParse. Works on scanned PDFs. Free with an okraPDF account.',
    h1: 'Extract PDF tables to Excel',
    subhead:
      'LlamaParse reads the tables in your PDF — digital or scanned — and each table becomes a sheet.',
    bullets: [
      'LlamaParse table extraction, not row guessing',
      'Reads scanned PDFs as well as digital ones',
      'Numbers arrive as numbers, so sums work in Excel',
      'Free with an okraPDF account',
    ],
    steps: [
      'Drop the PDF that contains the tables you need.',
      'Sign in and click Convert — the PDF uploads to a private okraPDF session and LlamaParse reads each table.',
      'Download an .xlsx with one sheet per table, ready for Excel or Google Sheets.',
    ],
    faq: [
      {
        q: 'Does this work on scanned PDFs?',
        a: 'Yes. LlamaParse reads scanned and digital PDFs, though clean digital PDFs give the most accurate results.',
      },
      {
        q: 'Can it handle bank statements and financial reports?',
        a: 'Yes. Each table becomes its own sheet. A long table that runs across pages may come back as one sheet per page.',
      },
      {
        q: 'Why do I need an account?',
        a: 'LlamaParse runs in the cloud, so the tool needs a free okraPDF account. Large files have a page limit, shown before you sign in.',
      },
      {
        q: 'What happens to my file?',
        a: 'The PDF stays on your device until you click Convert. It then uploads to a private okraPDF session for LlamaParse processing.',
      },
      {
        q: 'Can I get CSV or JSON instead of Excel?',
        a: 'This tool makes an .xlsx file. Use PDF to JSON for structured JSON, or the API for programmatic access.',
      },
    ],
  },
  {
    slug: 'extract',
    iconName: 'FileSpreadsheet',
    name: 'Extract Tables (AI)',
    description: 'AI table extraction for financial PDFs.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'AI PDF Table Extraction — Bank Statements, Filings | okraPDF',
    metaDescription: 'AI-powered table extraction for bank statements, 10-Ks, and financial PDFs. Handles merged cells, multi-page tables, and footnotes.',
    h1: 'Extract tables from PDFs with AI',
    subhead: 'Built for messy real-world filings — bank statements, 10-Ks, invoices.',
    bullets: [
      'Handles merged cells, sub-totals, and footnotes',
      'Multi-page tables stitched into one output',
      'Outputs to Excel, CSV, or JSON',
      'Files auto-deleted after 24 hours',
    ],
  },
  {
    slug: 'pdf-to-json',
    iconName: 'Braces',
    name: 'PDF to JSON',
    description: 'Convert any PDF to structured JSON in your schema.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to JSON — In Your Schema, One API Call | okraPDF',
    metaDescription: 'Convert PDF to JSON with custom field names, types, and derived fields. For AI agents, RAG pipelines, and structured extraction. Free to try.',
    h1: 'PDF to JSON, in your schema',
    subhead: 'Any PDF — invoices, contracts, bank statements, timesheets — returned as JSON that matches your exact field names, types, and computed fields. For pipelines feeding vector DBs or LLMs.',
    bullets: [
      'You define every field name, type, and derived value in a JSON Schema — the parser respects them exactly',
      'Computes derived fields (totals, booleans, deduped lists)',
      'Same parser benchmarked on bank statements and invoices',
      'One API call: okra extract --schema schema.json',
    ],
  },
  {
    slug: 'pdf-to-markdown',
    iconName: 'FileCode2',
    name: 'PDF to Markdown',
    description: 'Convert PDFs to Markdown for notes, RAG, and LLM context.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to Markdown — Convert PDF to MD Free | okraPDF',
    metaDescription:
      'Convert a PDF to Markdown with Gemini-powered ParseBench. Preserve headings, reading order, tables, and scanned text for RAG, notes, and LLM context.',
    h1: 'PDF to Markdown for RAG and notes',
    subhead:
      'Drop a paper, report, scan, or handbook. ParseBench uses Gemini to produce structured Markdown ready for a notes app, RAG pipeline, or LLM prompt.',
    bullets: [
      'Gemini reconstructs headings, lists, tables, and reading order',
      'Works with born-digital PDFs and scans in one conversion',
      'Your PDF uploads only after you click Convert',
      'Same parser later: okra context get <docId>',
    ],
    steps: [
      'Drop a PDF into the box above.',
      'Click Convert — your PDF uploads to the private ParseBench session and Gemini reconstructs its structure.',
      'Download the .md file or copy it into a notes app, repo, or prompt.',
    ],
    faq: [
      {
        q: 'Is my file uploaded?',
        a: 'The PDF stays on your device until you click Convert. It then uploads to a private okraPDF session for Gemini-powered processing.',
      },
      {
        q: 'Does this work on scanned PDFs?',
        a: 'Yes. The same Gemini vision parser handles scanned and born-digital PDFs, so a separate OCR pass is not required.',
      },
      {
        q: 'Will layout and tables survive?',
        a: 'ParseBench reconstructs headings, lists, reading order, and Markdown tables from the page image. Highly visual layouts may still be simplified.',
      },
      {
        q: 'Can I do this from the API?',
        a: 'Yes. Upload the PDF and call okra context get <docId> for cited page text and Markdown from the same parsing stack.',
      },
    ],
  },
  {
    slug: 'pdf-to-html',
    iconName: 'FileText',
    name: 'PDF to HTML',
    description: 'Open a PDF as a readable, accessible web page.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to HTML — Accessible Web Page Converter | okraPDF',
    metaDescription:
      'Convert a PDF into clean accessible HTML with figures, tables, and reading order preserved. Upload a PDF or paste a PDF URL.',
    h1: 'Convert PDF to accessible HTML',
    subhead:
      'Drop a PDF or paste a PDF URL. okraPDF opens it as a readable web page with cropped figures, intact tables, and improved reading order.',
    bullets: [
      'Creates a shareable HTML page from a PDF',
      'Preserves figures, tables, and source-page references',
      'Improves reading order for web and assistive-tech review',
      'Works from upload or public PDF URL',
    ],
    steps: [
      'Upload a PDF, or paste a direct URL to a PDF.',
      'okraPDF processes the file and creates a readable HTML page.',
      'Open the generated page, review it, and share the stable link.',
    ],
    faq: [
      {
        q: 'Is this the same as the homepage PDF-to-HTML demo?',
        a: 'Yes. The live PDF-to-HTML workflow now lives here as a dedicated tool instead of the main landing page.',
      },
      {
        q: 'Does it replace a full accessibility audit?',
        a: 'No. It creates a readable HTML version of the PDF. Use the PDF accessibility checker when you need PDF/UA, WCAG, or Section 508 evidence.',
      },
    ],
  },
  {
    slug: 'handwriting-ocr',
    iconName: 'PenTool',
    name: 'Handwriting OCR',
    description: 'Read handwritten notes and forms — benchmarked on IAM.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'Handwriting OCR — Read Handwritten Text | okraPDF',
    metaDescription:
      'Handwriting OCR for scanned notes, forms, and letters. 1.29% character error rate on the IAM benchmark — matches frontier VLMs, beats classic OCR. Free to try.',
    h1: 'Handwriting OCR, benchmarked on IAM',
    subhead:
      'Turn handwritten notes, filled forms, and letters into text. Measured at 1.29% character error rate on the IAM handwriting set — matching frontier vision models, where traditional OCR lands near 49%.',
    bullets: [
      'Upload a PDF, JPG, or PNG, then copy the text or download it',
      'Reads cursive and print: handwritten notes, filled forms, ledgers, letters',
      '1.29% character error rate on the IAM test set — beats the open TrOCR specialist model, on par with frontier VLMs',
      'Same /v1 parser: page reading order, tables, and bbox citations come with it',
      'One call: okra upload note.pdf, then okra read <docId>',
    ],
    faq: [
      {
        q: 'How accurate is okraPDF on handwriting?',
        a: "1.29% character error rate (CER) on the IAM test set — the standard handwriting benchmark. That sits in the 'excellent' band (under 2.5%), ahead of the open TrOCR-large specialist model and on par with frontier vision models. Traditional OCR (Tesseract) scores about 49% on the same lines.",
      },
      {
        q: 'Does it read cursive?',
        a: 'Yes. IAM is unconstrained English cursive and print from 600+ writers, and the 1.29% number is measured on exactly that. Messier or historical hands are harder — character error rate climbs as the source degrades.',
      },
      {
        q: 'How is this measured?',
        a: '120 lines sampled from the IAM-line test split (MIT-licensed, hosted on Hugging Face), scored as character error rate against the ground-truth transcriptions, with punctuation tokenization normalized identically across every system. Full method is on the page.',
      },
    ],
  },
  {
    slug: 'pdf-to-link',
    iconName: 'Link2',
    name: 'PDF to Link',
    description: 'Upload a PDF, get a shareable link.',
    category: 'convert-from',
    processingType: 'cloud',
    metaTitle: 'PDF to Link — Share a PDF With a URL | okraPDF',
    metaDescription:
      'Upload a PDF and get a clean shareable link, QR code, and embed code — unlisted, public, or password-protected. Free account, no install.',
    h1: 'Turn a PDF into a shareable link',
    subhead:
      'Upload an invoice, report, deck, or contract and get a readable okrapdf.dev link you can drop in an email — with public, unlisted, or password access you control.',
    // W.4.124 R4: replay showed a new visitor reading "Cloud processing" over
    // and over; the badge says what they get instead.
    badge: 'Free account · Unlisted by default',
    bullets: [
      'Preview the PDF and set a title before you create the link',
      'Pick access per link: public, unlisted, or password-protected',
      'Readable link like q2-report-x7k2pd.okrapdf.dev, not an opaque ID',
      'Same link from the API: POST /v1/host with a file_id',
      'QR code, embed code, and share buttons the moment the link is live',
    ],
    steps: [
      'Upload a PDF you own or have the rights to share (up to 5 MB).',
      'Preview the file, set a title, and choose who can open it.',
      'Create the link, then copy it, download its QR code, or grab the embed code.',
    ],
    faq: [
      {
        q: 'Can I password-protect the link?',
        a: 'Yes. Choose Password and set one; viewers enter it before the PDF loads. You can switch a link between public, unlisted, and password access anytime from your Documents dashboard.',
      },
      {
        q: 'Can I turn a PDF URL into a link?',
        a: 'No. This tool is upload-only — you publish PDFs you own or have the rights to share, not arbitrary URLs. To read a public PDF as an accessible web page instead, use the PDF to HTML tool.',
      },
      {
        q: 'Do I need an account?',
        a: 'Yes. Publishing needs a free okraPDF account, and every link stays with its document in your Documents dashboard. For automation, publish with POST /v1/host using an API key.',
      },
      {
        q: 'Will I lose my PDF when I sign up?',
        a: "No. When you click Create shareable link before you have an account, your PDF, title, and access settings are kept in this browser for up to 15 minutes while you sign up, and the link is created as soon as you are back, with no second upload. The file stays on your device until the link is created, a link password is never saved with it (a password link asks for it again), and a copy you don't come back for is deleted the next time PDF to Link opens after those 15 minutes.",
      },
      {
        q: 'What does the title do?',
        a: "It names the link on your Links page and in its embed code, and it shows on the password page of protected links. The PDF itself opens in the viewer's browser PDF reader.",
      },
      {
        q: 'Can I embed it or make a QR code?',
        a: 'Yes. When the link is live you can download a QR code (PNG or SVG) and copy an iframe embed code for your site. Embeds work for unlisted and public links.',
      },
      {
        q: 'Why is there a 5 MB limit?',
        a: 'It keeps publishing fast and free. Many larger PDFs fit under 5 MB after the Compress PDF tool.',
      },
      {
        q: 'Does the link expire?',
        a: 'No. A link stays live until you turn it off from your Documents dashboard.',
      },
    ],
  },
  {
    slug: 'pdf-to-jpg',
    iconName: 'FileImage',
    name: 'PDF to JPG',
    description: 'Convert PDF pages to JPG images.',
    category: 'convert-from',
    processingType: 'local',
    metaTitle: 'PDF to JPG — Convert Pages to Images Free | okraPDF',
    metaDescription: 'Convert PDF pages to high-quality JPG images. Choose resolution, download all as a zip. 100% in-browser, no upload.',
    h1: 'Convert PDF to JPG',
    bullets: [
      'Choose resolution: 72, 150, 300, or 600 DPI',
      'Download individual pages or all as a zip',
      '100% in-browser — files never upload',
      'Free, no signup, no watermark',
    ],
  },
  {
    slug: 'pdf-to-png',
    iconName: 'Image',
    name: 'PDF to PNG',
    description: 'Convert PDF pages to PNG with transparency.',
    category: 'convert-from',
    processingType: 'local',
    metaTitle: 'PDF to PNG — Pages to Images with Transparency | okraPDF',
    metaDescription: 'Convert PDF pages to PNG images with transparency support. Choose DPI, download all as zip. Free, in-browser, no upload.',
    h1: 'Convert PDF to PNG',
    bullets: [
      'Supports transparency for design workflows',
      'Choose 72, 150, 300, or 600 DPI',
      '100% in-browser — files never upload',
      'Free, no signup',
    ],
  },

  // Edit
  {
    slug: 'watermark',
    iconName: 'Droplets',
    name: 'Add Watermark',
    description: 'Add text or image watermark to PDF pages.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Add Watermark to PDF — Free Online Tool | okraPDF',
    metaDescription: 'Add text or image watermarks to PDF pages. Customize size, opacity, color, and position. Free, browser-only, no upload.',
    h1: 'Add a watermark to PDF',
    bullets: [
      'Text or image watermarks',
      'Adjust size, opacity, color, and rotation',
      'Apply to all pages or page ranges',
      '100% in-browser — files never upload',
    ],
  },
  {
    slug: 'page-numbers',
    iconName: 'Hash',
    name: 'Add Page Numbers',
    description: 'Add page numbers to PDF.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Add Page Numbers to PDF — Free Online | okraPDF',
    metaDescription: 'Add page numbers to your PDF. Choose position, format (1, i, A), font, and starting number. Free, in-browser, no upload.',
    h1: 'Add page numbers to PDF',
    bullets: [
      'Position: top/bottom, left/center/right',
      'Formats: 1, 1 of N, Page 1, i, I, A',
      'Custom font, size, and starting number',
      '100% in-browser — files never upload',
    ],
  },
  {
    slug: 'bates',
    iconName: 'Stamp',
    name: 'Bates Numbering',
    description: 'Sequential Bates numbering for legal discovery.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Bates Numbering — Legal PDF Stamping Free | okraPDF',
    metaDescription: 'Add sequential Bates numbers (e.g. SMITH-000001) to PDFs for legal discovery and document review. Free, in-browser, no upload.',
    h1: 'Add Bates numbers to PDF',
    subhead: 'Stamp PDFs with sequential numbers for legal discovery, e-filing, and document review.',
    bullets: [
      'Custom prefix and starting number (e.g. SMITH-000001)',
      'Choose digits, position, font, and color',
      'Stamp single PDFs or batches',
      '100% in-browser — files never upload',
    ],
  },
  {
    slug: 'redact',
    iconName: 'EyeOff',
    name: 'Redact PDF',
    description: 'Permanently remove sensitive text with True removal.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Redact PDF — Permanently Hide Sensitive Text | okraPDF',
    metaDescription: 'Use True removal to permanently remove covered PDF content by rebuilding pages as images. Overlay only covers content visually. Free, browser-only.',
    h1: 'Redact a PDF',
    subhead: 'Draw boxes, then export with True removal to permanently remove covered content.',
    bullets: [
      'True removal rebuilds pages as images; overlay only covers content visually',
      'Draw boxes with your mouse or touchscreen',
      '100% in-browser — files never upload',
      'Free, no signup, no watermark',
    ],
    steps: [
      'Open your PDF — it stays in your browser.',
      'Draw a box over each piece of sensitive text or image.',
      'Choose True removal so the covered content is deleted, not just hidden.',
      'Export the new PDF, then search it for the redacted terms to confirm they are gone.',
    ],
    faq: [
      {
        q: 'How do I redact a document so the text cannot be recovered?',
        a: 'Remove the content instead of covering it. True removal rebuilds each page as an image, so the text under a box no longer exists in the file. A black rectangle drawn on top leaves the text selectable and searchable.',
      },
      {
        q: 'What is the difference between True removal and overlay?',
        a: 'Overlay draws a box over the content but keeps the original text and images in the file. True removal rebuilds the page so the covered content is gone. Use True removal for anything you share outside your team.',
      },
      {
        q: 'Is my PDF uploaded?',
        a: 'No. Redaction runs entirely in your browser, and the file never leaves your device.',
      },
    ],
    guide: {
      href: '/blog/how-to-redact-documents/',
      label: 'How to redact a document permanently: metadata, scans, and verification',
    },
  },
  {
    slug: 'highlight',
    iconName: 'Highlighter',
    name: 'Highlight PDF',
    description: 'Add colored highlights to mark text.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Highlight PDF — Mark Text Online Free | okraPDF',
    metaDescription: 'Add colored highlights to PDF text. Multiple colors available, save and share. 100% in-browser, no upload required.',
    h1: 'Highlight text in PDF',
    bullets: [
      'Multiple highlight colors',
      'Click and drag to highlight any text',
      '100% in-browser — files never upload',
      'Free, no signup',
    ],
  },
  {
    slug: 'sign',
    iconName: 'PenTool',
    name: 'Sign PDF',
    description: 'Add your signature to PDF documents.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Sign PDF — Add Signature Online Free | okraPDF',
    metaDescription: 'Sign PDFs by drawing or uploading a signature image. Place anywhere on any page. 100% in-browser, no upload, no signup.',
    h1: 'Sign a PDF',
    bullets: [
      'Draw signature with mouse, trackpad, or touch',
      'Or upload a signature image',
      'Place anywhere on any page',
      '100% in-browser — files never upload',
    ],
  },
  {
    slug: 'crop',
    iconName: 'Crop',
    name: 'Crop Margins',
    description: 'Trim page margins.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Crop PDF Margins — Trim Pages Online | okraPDF',
    metaDescription: 'Crop and trim PDF page margins. Visual selection, batch apply across pages. Free, in-browser, no upload.',
    h1: 'Crop PDF margins',
    bullets: [
      'Visual margin selection',
      'Apply to single page or all pages',
      '100% in-browser — files never upload',
      'Free, no signup',
    ],
  },
  {
    slug: 'rotate',
    iconName: 'RotateCw',
    name: 'Rotate PDF',
    description: 'Rotate pages 90, 180, or 270 degrees.',
    category: 'edit',
    processingType: 'local',
    metaTitle: 'Rotate PDF — Fix Page Orientation Free | okraPDF',
    metaDescription: 'Rotate PDF pages 90, 180, or 270 degrees. Fix orientation for individual pages or the whole document. Free, in-browser.',
    h1: 'Rotate PDF pages',
    bullets: [
      'Rotate 90°, 180°, or 270°',
      'Apply to single page or all pages',
      '100% in-browser — files never upload',
      'Free, no signup',
    ],
  },
];

export const TOOLS_BY_SLUG: Record<string, ToolDef> = TOOLS.reduce(
  (acc, tool) => {
    acc[tool.slug] = tool;
    return acc;
  },
  {} as Record<string, ToolDef>,
);

export const TOOLS_INDEX_SEO = {
  title: 'Free PDF Tools — Edit, Convert & Organize | okraPDF',
  description:
    'Free PDF tools to merge, split, convert, compress, OCR, and check accessibility. Browser tools need no signup; cloud tools need an okraPDF account.',
  h1: 'Free PDF tools',
  subhead: 'Edit, convert, organize, and check PDFs — free and browser-first.',
} as const;

export function toolSeoFor(slug: string): {
  title: string;
  description: string;
  canonical: string;
} | null {
  const tool = TOOLS_BY_SLUG[slug];
  if (!tool) return null;
  return {
    title: tool.metaTitle,
    description: tool.metaDescription,
    canonical: `https://okrapdf.com/tools/${tool.slug}`,
  };
}
